import { useState, useCallback } from 'react';
import { ModerationReportContextType } from '../../../common/moderationTypes';
import { ModerationCategory, ModerationSubReason } from '../../../common/moderationReasonsConfig';
import { commonApiServiceAsync } from '../../scripts/services/commonApiService';

export interface ISubmitModerationReportParams {
  contextType: ModerationReportContextType;
  contextEntityId: number;
  reason: ModerationCategory;
  subReason: ModerationSubReason;
  description?: string;
  sourceUrl?: string;
  recaptchaCode?: string;
  tencentTicket?: string;
  tencentRandstr?: string;
}

export type ModerationReportState = 'idle' | 'loading' | 'success' | 'error';

interface IReportState {
  state: ModerationReportState;
  referenceCode: string | null;
  error: string | null;
  retryAfterSeconds: number | null;
}

const INITIAL_STATE: IReportState = {
  state: 'idle',
  referenceCode: null,
  error: null,
  retryAfterSeconds: null
};

export function useModerationReport() {
  const [reportState, setReportState] = useState<IReportState>(INITIAL_STATE);

  const submitReport = useCallback(async (params: ISubmitModerationReportParams): Promise<void> => {
    setReportState({ ...INITIAL_STATE, state: 'loading' });

    try {
      const data = await commonApiServiceAsync.submitModerationReport({
        context_type: params.contextType,
        context_entity_id: params.contextEntityId,
        reason: params.reason,
        sub_reason: params.subReason,
        description: params.description || null,
        source_url: params.sourceUrl || null,
        recaptcha_code: params.recaptchaCode || null,
        tencent_ticket: params.tencentTicket || null,
        tencent_randstr: params.tencentRandstr || null
      });

      setReportState({
        ...INITIAL_STATE,
        state: 'success',
        referenceCode: data.referenceCode
      });
    } catch (err: any) {
      const errorBody = err?.response?.body || {};
      const errorData = errorBody.data || errorBody;
      setReportState({
        ...INITIAL_STATE,
        state: 'error',
        error: errorData.error || 'generic',
        retryAfterSeconds: errorData.retryAfterSeconds || null
      });
    }
  }, []);

  const reset = useCallback(() => {
    setReportState(INITIAL_STATE);
  }, []);

  const setError = useCallback((error: string) => {
    setReportState({ ...INITIAL_STATE, state: 'error', error });
  }, []);

  return {
    submitReport,
    reset,
    setError,
    state: reportState.state,
    referenceCode: reportState.referenceCode,
    error: reportState.error,
    retryAfterSeconds: reportState.retryAfterSeconds
  };
}
